import { useState } from "react";
import { useDraggable } from "@dnd-kit/core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle, GripVertical, StickyNote } from "lucide-react";
import { GearItemWithPacking } from "@/types/packing";

interface PackingGearItemProps {
  item: GearItemWithPacking;
  canEdit: boolean;
  onTogglePacked: (itemId: string, currentStatus: boolean) => void;
  isSelected?: boolean;
  onItemClick?: (itemId: string, event: React.MouseEvent) => void;
  isBeingDragged?: boolean;
}

const categoryConfig: Record<string, { icon: string; color: string }> = {
  'Cameras': { icon: '🎥', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  'Power': { icon: '🔋', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  'Monitoring': { icon: '🖥', color: 'bg-purple-100 text-purple-800 border-purple-200' },
  'Camera Support': { icon: '🛠', color: 'bg-gray-100 text-gray-800 border-gray-200' },
  'Wireless': { icon: '📡', color: 'bg-teal-100 text-teal-800 border-teal-200' },
  'Audio': { icon: '🎧', color: 'bg-green-100 text-green-800 border-green-200' },
  'Storage': { icon: '💾', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  'Lighting': { icon: '💡', color: 'bg-orange-100 text-orange-800 border-orange-200' },
};

export function PackingGearItem({ 
  item, 
  canEdit, 
  onTogglePacked, 
  isSelected = false,
  onItemClick,
  isBeingDragged = false
}: PackingGearItemProps) {
  const [showNote, setShowNote] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({
    id: item.id,
    disabled: !canEdit,
  });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
  };

  const handleClick = (event: React.MouseEvent) => {
    if (onItemClick) {
      onItemClick(item.id, event);
    }
  };

  const categoryInfo = categoryConfig[item.category] || {
    icon: '📦',
    color: 'bg-gray-100 text-gray-800 border-gray-200'
  };

  const getItemClass = () => {
    let baseClass = `
      group flex items-center gap-2 sm:gap-3 p-2 sm:p-2 rounded-lg transition-all duration-200 min-h-[40px] sm:min-h-[44px]
      ${item.is_packed ? 'opacity-50' : ''}
      ${isBeingDragged ? 'opacity-0' : ''}
      ${isDragging ? 'opacity-50 scale-105 rotate-2 shadow-lg z-50' : ''}
      ${isSelected ? 'ring-1 ring-primary/50' : ''}
    `;
    
    if (!item.color) {
      return baseClass + (isSelected ? 'bg-primary/5' : 'bg-white/5 border border-white/10');
    }
    
    switch (item.color) {
      case 'blue': return baseClass + 'bg-blue-500/20 border border-blue-500/30';
      case 'green': return baseClass + 'bg-green-500/20 border border-green-500/30';
      case 'yellow': return baseClass + 'bg-yellow-500/20 border border-yellow-500/30';
      case 'red': return baseClass + 'bg-red-500/20 border border-red-500/30';
      case 'purple': return baseClass + 'bg-purple-500/20 border border-purple-500/30';
      default: return baseClass + (isSelected ? 'bg-primary/5' : 'bg-white/5 border border-white/10');
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleClick}
      className={getItemClass()}
      {...listeners}
      {...attributes}
    >
      {canEdit && (
        <GripVertical className="h-3 w-3 text-muted-foreground/30 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab flex-shrink-0 hidden sm:block" />
      )}
      
      <button
        onClick={(e) => {
          e.stopPropagation();
          onTogglePacked(item.id, item.is_packed);
        }}
        disabled={!canEdit}
        className="flex-shrink-0 transition-colors duration-200 p-1 min-w-[20px] min-h-[20px] sm:min-w-[24px] sm:min-h-[24px] flex items-center justify-center"
      >
        {item.is_packed ? (
          <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4 text-green-500" />
        ) : (
          <Circle className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground/50 hover:text-green-500" />
        )}
      </button>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1 sm:gap-2">
          <p className={`font-medium text-xs sm:text-sm ${item.is_packed ? 'line-through text-muted-foreground' : ''} truncate`}>
            {item.name}
          </p>
          {item.notes && (
            <Button
              variant="ghost"
              size="sm"
              className="h-3 w-3 sm:h-4 sm:w-4 p-0 opacity-40 hover:opacity-100 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowNote(!showNote);
              }}
            >
              <StickyNote className="h-2 w-2 sm:h-2 sm:w-2" />
            </Button>
          )}
        </div>
        
        <div className="text-xs text-muted-foreground/70 mt-0.5 truncate">
          <span>Qty {item.quantity} • {categoryInfo.icon}</span>
          <span className="hidden sm:inline"> {item.category}</span>
        </div>
        
        {showNote && item.notes && (
          <div className="mt-1 sm:mt-2 p-2 bg-muted rounded text-xs text-muted-foreground">
            {item.notes}
          </div>
        )}
      </div>
      
      {isSelected && (
        <div className="text-primary">
          <CheckCircle2 className="h-3 w-3 sm:h-4 sm:w-4" />
        </div>
      )}
    </div>
  );
}